import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, ShieldCheck, Package, Users } from "lucide-react";

const ACCOUNT_TYPES = ["MANUFACTURER", "RETAILER", "REPAIRER", "INSTALLER", "INSURER",
  "SERVICE_PROVIDER", "RECONDITIONER", "MARKETPLACE", "PROPERTY_PROFESSIONAL"];

const PERKS = [
  { icon: Package, title: "Passeports numériques", text: "Publie tes produits, chacun devient un passeport OMY officiel." },
  { icon: ShieldCheck, title: "Garanties officielles", text: "Pousse la garantie constructeur et la notice vérifiées à tes clients." },
  { icon: Users, title: "Leads qualifiés", text: "Reste connecté au cycle de vie du produit, sans données personnelles." },
];

export default function Business() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [reg, setReg] = useState({ company_name: "", account_type: "MANUFACTURER", plan: "starter" });

  useEffect(() => {
    api.get("/business/plans").then((r) => setPlans(r.data));
    if (user?.business) navigate("/business-app", { replace: true });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const register = async () => {
    if (!reg.company_name) return toast.error("Nom de l'entreprise requis");
    await api.post("/business/register", reg);
    await refreshUser();
    toast.success("Compte OMY Business activé");
    navigate("/business-app");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">OMY Business</h1>
        <p className="text-muted-foreground text-sm mt-1">Console B2B pour fabricants, revendeurs, réparateurs, assureurs. OMY reste gratuit pour le particulier.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {PERKS.map((p, i) => (
          <div key={i} className="omy-glass rounded-2xl p-5">
            <p.icon size={18} className="text-indigo-300 mb-3" />
            <p className="font-display font-medium text-sm">{p.title}</p>
            <p className="text-xs text-muted-foreground mt-1">{p.text}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-${p.id}`} className="omy-glass rounded-2xl p-5 text-center">
            <p className="font-display font-medium capitalize">{p.name}</p>
            <p className="text-2xl font-display font-semibold mt-2 text-indigo-300">{p.price ? `${p.price}€` : "Sur devis"}</p>
            {p.price != null && <p className="text-xs text-muted-foreground">/ mois</p>}
          </div>
        ))}
      </div>

      <div className="omy-glass rounded-2xl p-6 space-y-4">
        <h2 className="font-display font-medium flex items-center gap-2"><Building2 size={18} className="text-indigo-300" /> Activer un compte Business</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Nom de l'entreprise</Label>
            <Input data-testid="biz-name-input" value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} className="mt-1 bg-black/30" placeholder="Samsung France" /></div>
          <div><Label>Type de compte</Label>
            <Select value={reg.account_type} onValueChange={(v) => setReg({ ...reg, account_type: v })}>
              <SelectTrigger data-testid="biz-type-select" className="mt-1 bg-black/30"><SelectValue /></SelectTrigger>
              <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Plan</Label>
            <Select value={reg.plan} onValueChange={(v) => setReg({ ...reg, plan: v })}>
              <SelectTrigger data-testid="biz-plan-select" className="mt-1 bg-black/30"><SelectValue /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <Button data-testid="biz-register-btn" onClick={register} className="bg-indigo-500 hover:bg-indigo-500/90 rounded-full">Activer</Button>
      </div>
    </div>
  );
}
